import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import CourseDetail from './sections/course-detail';
import NoCourse from './no-course-view';
import useProjectCourse from './hooks/use-project-course';
import CourseCertificate from './sections/course-certificate';
import useCourseRequirement from './hooks/use-course-requirement';
import CourseRequirement from './sections/course-requirement';
import CourseDeleteModal from './modals/course-delete-modal';
import { lazy, Suspense, useState } from 'react';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import Switch from '@ui/switch/switch';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { updateProjectSettings } from '@/store/slices/project';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { EventSettings } from '@/types/projects.types';
import { ICourseCertificate } from '@/types/courses';

const CourseAddRequirementModal = lazy(() => import('./modals/course-add-requirement-modal'));
const CourseDeleteRequirementModal = lazy(() => import('./modals/course-delete-requirement-modal'));

const CourseMain = () => {
  const {
    course,
    setCourse,
    working,
    workingEvent,
    handleSave,
    handleDelete,
    courseCanBeAddedToEvent,
    initializeCourse,
  } = useProjectCourse();

  const {
    modalOpen,
    currentRequirement,
    requirementWorking,
    requirementToDelete,
    setCurrentRequirement,
    setRequirementToDelete,
    openRequirementModal,
    closeRequirementModal,
    handleCreateRequirement,
    handleUpdateRequirement,
    handleDeleteRequirement,
    handleEditRequirement,
    requirementToUpdate,
    canAddRequirement,
    defaultSettings,
    defaultNewRequirement,
    canSaveNewRequirement,
  } = useCourseRequirement({
    course,
    setCourse,
  });

  const dispatch = useAppDispatch();
  const workingProject = useAppSelector(getWorkingProject);
  const coursesEnabled = !!workingProject?.settings.course_enabled;

  const toggleCourses = (_sKey: string, on: boolean) => {
    if (!workingProject) return;

    const projectSettings: EventSettings = {
      ...workingProject.settings,
      course_enabled: on,
    };
    dispatch(updateProjectSettings({ event_uuid: workingProject.uuid, settings: projectSettings }));
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const toggleDeleteModal = () => setDeleteModalOpen((prevState) => !prevState);

  const handleConfirmDelete = async () => {
    await handleDelete();
    setDeleteModalOpen(false);
  };

  if (working && !course) {
    return (
      <div className="flex ai-center jc-center" style={{ height: 'calc(50vh)' }}>
        <WaitingIndicator />
      </div>
    );
  }

  if (!course && !working) {
    return (
      <NoCourse
        setCourse={setCourse}
        courseCanBeAddedToEvent={courseCanBeAddedToEvent}
        initializeCourse={initializeCourse}
        working={working}
      />
    );
  }

  const handleCertificateUpdate = (updatedCert: ICourseCertificate) => {
    if (course) {
      setCourse({
        ...course,
        certificate: updatedCert,
      });
    }
  };

  return (
    <>
      <Container className="flex-column gap-12 padding-16 radius-12" design="GlobalCont1">
        <Container borderRadius="12px" className="flex ai-center jc-space-between margin-t--6">
          <Text tag="h6" className="H6">
            Attendee Course
          </Text>
          <Container className="flex ai-center gap-10">
            <Button
              data-testid="course-edit-delete-btn"
              onClick={toggleDeleteModal}
              design="LocalBttnSec"
              className="flex ai-center gap-6"
            >
              <Icon name={ICONS.TRASH} size={14} />
              <Text className="L2">Remove</Text>
            </Button>
            <Switch on={coursesEnabled} onClick={toggleCourses} value="enable_course" />
          </Container>
        </Container>
        <CourseDetail handleSave={handleSave} course={course} />
        <CourseRequirement
          course={course}
          workingEvent={workingEvent}
          working={working}
          handleEditRequirement={handleEditRequirement}
          setRequirementToDelete={setRequirementToDelete}
          canAddRequirement={canAddRequirement}
          openRequirementModal={openRequirementModal}
          defaultNewRequirement={defaultNewRequirement}
          defaultSettings={defaultSettings}
        />
        <CourseCertificate
          course={course}
          savingCertificate={false}
          workingCertificate={course?.certificate}
          onCertificateUpdate={handleCertificateUpdate}
        />
      </Container>
      <Suspense fallback={null}>
        {modalOpen && (
          <CourseAddRequirementModal
            requirementWorking={requirementWorking}
            open={modalOpen}
            onClose={closeRequirementModal}
            workingEvent={workingEvent}
            requirements={course?.requirements}
            newRequirement={currentRequirement}
            setNewRequirement={setCurrentRequirement}
            onCreateOrUpdateRequirement={requirementToUpdate ? handleUpdateRequirement : handleCreateRequirement}
            defaultSettings={defaultSettings}
            defaultNewRequirement={defaultNewRequirement}
            canSaveNewRequirement={canSaveNewRequirement}
          />
        )}
      </Suspense>
      <Suspense fallback={null}>
        {requirementToDelete && (
          <CourseDeleteRequirementModal
            requirementToDelete={requirementToDelete}
            setRequirementToDelete={setRequirementToDelete}
            course={course}
            deleteRequirement={handleDeleteRequirement}
            requirementWorking={requirementWorking}
          />
        )}
      </Suspense>
      <CourseDeleteModal
        showDeleteModal={deleteModalOpen}
        setShowDeleteModal={toggleDeleteModal}
        courseName={course?.name.base || ''}
        onDelete={handleConfirmDelete}
        working={working}
      />
    </>
  );
};

export default CourseMain;
